import { Checkbox, message, Modal } from '@maxtropy/components';
import React, { useEffect, useMemo, useState, useRef } from 'react';
import styles from '../index.module.scss';
import {
  apiV2AreaDataPropertyListPost,
  apiV2AreaDataPropertyRelationSavePost,
} from '@maxtropy/device-customer-apis-v2';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import { DataProperty } from '@/api/reportStrategy';

interface DeviceType {
  id?: number;
  name?: string;
  dataPropertySimpleList?: DataProperty[];
}

interface Props {
  areaId?: number;
  open: boolean;
  onClose: () => void;
}

const StatisticalDataModal: React.FC<Props> = ({ open, onClose, areaId }) => {
  const [data, setData] = useState<DeviceType[]>([]);
  const [checkedList, setCheckedList] = useState<string[]>([]);
  const contentRef = useRef<HTMLDivElement>(null);

  // 每次打开弹框时回到顶部
  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => {
        if (contentRef.current) {
          contentRef.current.scrollTop = 0;
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [open]);

  // 获取所有数据点
  useEffect(() => {
    if (!areaId || !open) return;

    apiV2AreaDataPropertyListPost({ areaId }).then(allDataRes => {
      // 所有数据点
      const allList = allDataRes.list ?? [];

      // 构建展示数据
      const newList: DeviceType[] = allList.map(item => ({
        id: item.id,
        name: item.name,
        dataPropertySimpleList: (item.dataPropertySimpleList ?? []).map(prop => ({
          ...prop,
          key: String(prop.id),
        })),
      }));

      setData(newList);
      setCheckedList([]);
    });
  }, [areaId, open]);

  const indeterminate = useMemo(() => {
    const options = data
      .map(item => item.dataPropertySimpleList?.map(k => k.key))
      .flat()
      .filter(k => k !== undefined) as string[];
    return checkedList.length > 0 && checkedList.length < options.length;
  }, [checkedList.length, data]);

  const checkAll = useMemo(() => {
    const options = data
      .map(item => item.dataPropertySimpleList?.map(k => k.key))
      .flat()
      .filter(k => k !== undefined) as string[];
    return checkedList.length === options.length;
  }, [checkedList.length, data]);

  const submit = () => {
    const checkedDataPropertyIds = checkedList.map(key => Number(key)).filter(id => !isNaN(id));

    apiV2AreaDataPropertyRelationSavePost({
      areaId,
      dataPropertyIds: checkedDataPropertyIds,
    }).then(() => {
      message.success('应用成功');
      onClose();
    });
  };

  // 全选
  const onCheckAllChange = (e: CheckboxChangeEvent) => {
    const checked = e.target.checked;
    const options = data
      .map(item => item.dataPropertySimpleList?.map(k => k.key))
      .flat()
      .filter(k => k !== undefined) as string[];
    setCheckedList(checked ? options : []);
  };

  // 每个类目的全选
  const onCategoryCheckAll = (e: CheckboxChangeEvent, item: DeviceType) => {
    const checked = e.target.checked;
    const itemKeys = item.dataPropertySimpleList?.map(k => k.key!).filter(k => k) as string[];
    if (checked) {
      const otherKeys = checkedList.filter(k => !itemKeys.includes(k));
      setCheckedList([...otherKeys, ...itemKeys]);
    } else {
      setCheckedList(checkedList.filter(k => !itemKeys.includes(k)));
    }
  };

  // 获取类目的半选状态
  const getCategoryIndeterminate = (item: DeviceType) => {
    const itemKeys = item.dataPropertySimpleList?.map(k => k.key!) as string[];
    const checkedCount = checkedList.filter(k => itemKeys.includes(k)).length;
    return checkedCount > 0 && checkedCount < itemKeys.length;
  };

  // 获取类目的全选状态
  const getCategoryCheckAll = (item: DeviceType) => {
    const itemKeys = item.dataPropertySimpleList?.map(k => k.key!) as string[];
    const checkedCount = checkedList.filter(k => itemKeys.includes(k)).length;
    return checkedCount === itemKeys.length && itemKeys.length > 0;
  };

  const onChangeItem = (checkedValues: string[], itemValues?: string[]) => {
    // 找出不在当前 group 的 keys
    const otherGroupCheckedVals = checkedList.filter(k => !itemValues?.includes(k));
    setCheckedList([...otherGroupCheckedVals, ...checkedValues]);
  };

  return (
    <Modal
      title="选择统计数据"
      size="large"
      open={open}
      onCancel={onClose}
      onOk={submit}
      okText="应用到当前设备"
      className={styles.statisticalModal}
    >
      <div className={styles.modalContent} ref={contentRef}>
        <div className={styles.checkedTip}>
          <Checkbox onChange={onCheckAllChange} indeterminate={indeterminate} checked={checkAll}>
            全选
          </Checkbox>
          <span>
            已选择 <span className={styles.checkedNum}>{`${checkedList?.length ?? 0}`}</span> 项
          </span>
        </div>
        {data.map((item, index) => (
          <div className={styles.dataGroup} key={item.id ?? index}>
            <div className={styles.dataGroupDivider} />
            <div>
              <Checkbox
                indeterminate={getCategoryIndeterminate(item)}
                checked={getCategoryCheckAll(item)}
                onChange={e => onCategoryCheckAll(e, item)}
              >
                {item.name}
              </Checkbox>
            </div>
            <Checkbox.Group
              className={styles.checkboxContainer}
              options={item.dataPropertySimpleList?.map(i => ({
                label: i.name,
                value: i.key!,
              }))}
              value={checkedList}
              onChange={(checkedValues: any) => {
                const itemValues = item.dataPropertySimpleList?.map(k => k.key!);
                onChangeItem(checkedValues, itemValues);
              }}
            />
          </div>
        ))}
      </div>
    </Modal>
  );
};

export default StatisticalDataModal;
